import {
  useWishlist,
  useWishlistProducts,
  useLocalWishlist,
  useCart,
  useAddItemsToCart,
  useCustomer,
} from 'hooks';
import { useEffect, useState } from 'react';
import Button from 'components/shared/button';

import ProductCard from 'components/shared/productCard';
import getShopifyData from 'lib/shopify/getShopifyData';
import SiteLink from 'components/shared/sitelink';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useRouter } from 'next/router';
import { useSettings } from 'contexts/SettingContext';
import { getShopifyGid } from 'lib/shopify/shopifyGid';
import useQuickAddToCartTracking from 'hooks/tracking/useQuickAddToCartTracking';
import useProductAddedTracking from 'hooks/tracking/useProductAddedTracking';
import useIsMobile from 'hooks/utils/useIsMobile';
import trackWishlist from 'lib/utils/trackWishlist';
import { useGlobalModules } from 'contexts';

import { getSelectedSalonNo, getStorageCustomerSalonNo } from 'lib/shopify';
import styles from './wishlist.module.scss';

const Wishlist = ({ locale }) => {
  const { wishlistProducts } = useWishlistProducts(locale);
  const { removeFromWishlist } = useWishlist();
  const { localWishlist } = useLocalWishlist();
  const [products, setProducts] = useState([]);
  const [shopifyData, setShopifyData] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const { cart } = useCart();
  const router = useRouter();
  const addItems = useAddItemsToCart();
  const { addProduct, setSaveItemSelectionList } = useSettings();
  const { customer } = useCustomer();
  const isMobile = useIsMobile();

  const salonNo = getStorageCustomerSalonNo(locale);
  const selectedSalonNo = getSelectedSalonNo(locale);

  const globalModules = useGlobalModules();
  const { links, translations } = globalModules || {};
  const buttonEmptyWishlist = globalModules?.buttons?.button?.emptyWishlist;

  const t = key => translations?.wishlist?.[key] || '';

  useEffect(() => {
    const fetchAllProducts = async () => {
      const data = await getShopifyData({ productIds: localWishlist, locale });
      setShopifyData(data);

      // Merge cardShopifyData into each product
      const productsWithShopifyData = wishlistProducts.map((prod, index) => ({
        ...prod,
        cardShopifyData: data[index],
      }));

      setProducts(productsWithShopifyData);
    };
    fetchAllProducts();
  }, [wishlistProducts, localWishlist, locale]);

  useEffect(() => {
    document.body.style.paddingBottom = isMobile && wishlistProducts.length > 0 ? '6rem' : '';
    return () => {
      document.body.style.paddingBottom = '';
    };
  }, [isMobile, wishlistProducts]);

  const handleAddAllToCart = async () => {
    const allProducts = shopifyData.reduce((prev, shopifyProduct) => {
      const relatedProducts = shopifyProduct.relatedProducts || [];
      return [...prev, shopifyProduct.variantId, ...relatedProducts];
    }, []);

    const productsDataForCheckout = [...new Set(allProducts)].map(id => ({
      variantId: id,
      quantity: 1,
    }));

    await addItems(productsDataForCheckout);

    shopifyData.forEach((prod, i) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuickAddToCartTracking({
        product: {
          name: prod.productTitle,
          id: products?.[i]?.shopifyData?.variants?.[0]?.product_id,
          productSpecs: products?.[i]?.productSpecs,
          price: prod.productPrice,
          index: i,
          quantity: 1,
          image_url: products?.[i]?.image?.baseUrl,
          compare_at_price: prod?.productCompareAtPrice || null,
        },
        list: `WishList`,
        setSaveItemSelectionList,
        locale,
      });
      // Universal Analytics
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useProductAddedTracking({
        affiliatedSalonNo: salonNo,
        selectedSalonNo,
        product: {
          name: prod.productTitle,
          id: products?.[i]?.shopifyData.variants[0]?.product_id,
          brand: 'Keune',
          category: products?.[i]?.productSpecs?.find(
            ({ type }) =>
              type === 'Producttype' || type === 'Type de produit' || type === 'Product type'
          )?.value,
          price: prod.productPrice,
          discount:
            prod.productCompareAtPrice !== null
              ? prod.productCompareAtPrice - prod.productPrice
              : null,
          quantity: 1,
        },
        location: { action: router },
      });

      if (!prod.isFreeItem) {
        addProduct({
          title: prod.productTitle,
          slug: prod.productHandle,
          variantId: getShopifyGid('ProductVariant', products?.[i]?.shopifyData.variants[0].id),
        });
      }
    });
  };

  const handleRemoveFromWishlist = async (product, index) => {
    const GID = getShopifyGid('Product', product.shopifyData.variants[0].product_id);
    await removeFromWishlist([GID]);
    trackWishlist(
      product,
      shopifyData[index],
      'remove_from_wishlist',
      `Category: Wish List`,
      index
    );
  };

  return (
    <section className={styles.wishlist}>
      <div className={styles.wishlistHeader}>
        <h1>{t('title')}</h1>
        {!customer?.isLoggedIn && (
          <div className={styles.loginRequest}>
            <p>{t('loginRequestText')}</p>
            <div className={styles.buttonsSection}>
              <SiteLink
                locale={locale}
                href={addLocaleToHref('/account/register/', locale, router)}
              >
                <button type="button" className={styles.buttonBlack}>
                  {t('buttonSignup')}
                </button>
              </SiteLink>
              <SiteLink locale={locale} href={addLocaleToHref('/account/login/', locale, router)}>
                <button type="button">{t('buttonLogin')}</button>
              </SiteLink>
            </div>
          </div>
        )}
      </div>

      {products?.length > 0 ? (
        <>
          <div className={styles.productsContainer}>
            {products?.map((product, index) => (
              <div className={styles.productWrapper} key={product.id}>
                <ProductCard
                  locale={locale}
                  itemData={product}
                  itemIndex={index}
                  pageTitle="Wish List"
                />
                <div className={styles.removeProductContainer}>
                  <button
                    type="button"
                    className={styles.removeProductButton}
                    onClick={() => handleRemoveFromWishlist(product, index)}
                  >
                    {t('buttonRemoveProduct')}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.addAllProducts}>
            <button type="button" className={styles.buttonBlack} onClick={handleAddAllToCart}>
              {t('buttonAddAllProducts')}
            </button>
          </div>
        </>
      ) : (
        <div className={styles.emptyContainer}>
          <div className={styles.notification}>{t('emptyWishlistText')}</div>
          <SiteLink
            locale={locale}
            href={addLocaleToHref(links?.allProductsLink?.slug, locale, router)}
          >
            <Button
              locale={locale}
              className={styles.checkoutButton}
              button={buttonEmptyWishlist}
            />
          </SiteLink>
        </div>
      )}
    </section>
  );
};

export default Wishlist;
